/**
 * ContractorContacts.tsx — F12.M Etap 1.5 (Kreator Listy — replikacja).
 *
 * Lista wszystkich kontaktów kontrahentów (decyzja koordynatora 2026-05-13:
 * Kontakty = pełnoprawny 5. moduł). Backend: GET /api/contractor-contacts
 * z search + headerFilters (Kreator Listy) + Model B visibility.
 *
 * Akcje:
 *  - Klik wierszem → /contractor-contacts/:id (detail)
 *  - „+ Dodaj kontakt" → /contractor-contacts/new (picker kontrahenta)
 *  - Klik na nazwę kontrahenta w kolumnie → /contractors/:cId (link)
 *
 * Akcje zbiorcze (PHASE 25) przez wspólny hook `useListActions`: dropdown „Akcje"
 * nad listą + prawy klik + kebab w wierszu. Archiwizacja (miękkie usunięcie) rozgłasza
 * DELETE po ID na trasę zagnieżdżoną `/contractors/:cId/contacts/:id` (kontakt nie ma
 * płaskiej trasy `/contractor-contacts/:id` do zapisu — tylko GET) — dlatego idzie
 * escape-hatchem `handlers.archive`, nie generykiem. Przywracanie/eksport/import nie
 * mają jeszcze endpointu → „wkrótce" (widoczne, wyszarzone).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Pencil, Phone, Plus, Star, Users } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  RowActionsMenu,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { GroupChipBar } from '@/components/groups/GroupChipBar';
import { useToast } from '@/contexts/ToastContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { recordLockReason } from '@/lib/documents/record-lock-reason';
import { useListActions } from '@/lib/list-actions/useListActions';
import { allowedViewModesForList } from '@/lib/list-view-support';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const CONTACTS_KEY = 'contractor-contacts';

interface ContactRow {
  id: string;
  contractorId: string;
  contractorName: string;
  contractorShortName: string | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  isPrimary: boolean;
  isDecisionMaker: boolean;
  gcrecord: string | null;
}

interface ListResponse {
  contacts: ContactRow[];
  total: number;
  limit: number;
  offset: number;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function fullName(c: ContactRow): string {
  const parts = [c.title, c.firstName, c.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

export function ContractorContacts() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // Przełącznik „kosza" (gcrecord) zdjęty z kartotek (PHASE 25): „Usuń" = miękkie usunięcie
  // bez widoku odzyskiwania w UI. Archiwum (flaga) jest wyłącznie na dokumentach.
  /**
   * Filtr chipów nad listą = GRUPY ze słownika `contractor_contact_groups` (domknięcie
   * projektu grup, 2026-08-19). Wiele grup naraz, semantyka OR. Kategoria NIE filtruje listy.
   */
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Filtry per kolumna z popupu w nagłówku + wiersz „Szukaj…" pod nagłówkami.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  const forcedListLayoutId = useForcedListLayoutId('contractor_contacts');
  const tableId = forcedListLayoutId
    ? `contractor_contacts::layout-${forcedListLayoutId}`
    : 'contractor_contacts';

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz kontaktu
  // znał kontekst pozycji menu — URL detalu jest osobną nawigacją, nie dziedziczy
  // query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Aktywna firma (CompanySwitcher) — listing scope'owany po stronie backendu
  // przez `?companyId=` (Model B, `resolveContractorListVisibility`). Brak
  // (tryb "Wszystkie firmy") => unia po widocznych firmach.
  const companyScope = useActiveCompanyScope();
  const companyId = companyIdParam(companyScope);

  // Definicja kolumn dla ConfigurableTable. Filtrowalne pola tekstowe mają
  // `fieldSlug` zgodny z whitelistą backendu (search + distinct + headerFilters).
  // Kolumna „Kontrahent" to relacja (join) — bez popupu filtra.
  const columns = useMemo<ColumnDefinition<ContactRow>[]>(
    () => [
      {
        id: 'name',
        accessor: (row) => fullName(row),
        header: t('tenant.contractorContacts.col.name'),
        defaultVisible: true,
        defaultWidth: 260,
        sortable: true,
        hideable: false,
        headerFilter: { category: 'text', fieldSlug: 'last_name' },
        renderCell: (_value, row) => {
          const isDeleted = row.gcrecord !== null;
          return (
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`font-medium ${isDeleted ? 'text-text-tertiary' : 'text-text-primary'}`}
                >
                  {fullName(row)}
                </span>
                {row.isPrimary && (
                  <Badge color="amber">
                    <Star size={10} aria-hidden="true" />
                  </Badge>
                )}
                {row.isDecisionMaker && (
                  <Badge color="blue">{t('tenant.contractorContacts.badge.decisionMaker')}</Badge>
                )}
                {isDeleted && (
                  <Badge color="red">{t('tenant.contractorContacts.badge.deleted')}</Badge>
                )}
              </div>
              {row.department && <div className="text-xs text-text-tertiary">{row.department}</div>}
            </div>
          );
        },
      },
      {
        id: 'contractor',
        accessor: 'contractorId',
        header: t('tenant.contractorContacts.col.contractor'),
        defaultVisible: true,
        defaultWidth: 240,
        sortable: false,
        renderCell: (_value, row) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Link
              to={`/contractors/${row.contractorId}`}
              className="text-accent-700 hover:underline dark:text-accent-300"
            >
              {row.contractorShortName || row.contractorName}
            </Link>
          </div>
        ),
      },
      {
        id: 'position',
        accessor: 'position',
        header: t('tenant.contractorContacts.col.position'),
        defaultVisible: true,
        defaultWidth: 180,
        sortable: true,
        headerFilter: { category: 'text', fieldSlug: 'position' },
        renderCell: (value) =>
          value ? (
            <span className="text-text-secondary">{String(value)}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
      {
        id: 'email',
        accessor: 'email',
        header: t('tenant.contractorContacts.col.email'),
        defaultVisible: true,
        defaultWidth: 220,
        sortable: true,
        headerFilter: { category: 'text', fieldSlug: 'email' },
        renderCell: (value) =>
          value ? (
            <a
              href={`mailto:${String(value)}`}
              className="inline-flex items-center gap-1 text-text-secondary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail size={12} aria-hidden="true" />
              {String(value)}
            </a>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
      {
        id: 'phone',
        accessor: (row) => row.phone ?? row.mobile,
        header: t('tenant.contractorContacts.col.phone'),
        defaultVisible: true,
        defaultWidth: 160,
        sortable: false,
        headerFilter: { category: 'text', fieldSlug: 'phone' },
        renderCell: (value) =>
          value ? (
            <span className="inline-flex items-center gap-1 text-text-secondary">
              <Phone size={12} aria-hidden="true" />
              {String(value)}
            </span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
    ],
    [t],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains) +
  // (3) aktywny szybki filtr (preset).
  const serializedHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }

    return merged.length > 0 ? JSON.stringify(merged) : undefined;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);

  const queryKey = [
    CONTACTS_KEY,
    {
      q: debouncedSearch,
      groups: selectedGroups.length > 0 ? [...selectedGroups].sort() : null,
      headerFilters: serializedHeaderFilters,
      companyId: companyId ?? null,
    },
  ];
  const listQ = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      for (const code of selectedGroups) params.append('groups', code);
      if (serializedHeaderFilters) params.set('headerFilters', serializedHeaderFilters);
      if (companyId) params.set('companyId', companyId);
      params.set('limit', '100');
      const res = await api.get<ListResponse>(`/contractor-contacts?${params.toString()}`);
      return res.data;
    },
  });

  const contacts = listQ.data?.contacts ?? [];

  // Zaznaczenie jest kontrolowane przez tę stronę — po zmianie zbioru danych (filtr /
  // wyszukiwanie / grupy / firma / przełącznik archiwum) przycinamy je do rekordów WCIĄŻ
  // widocznych. Archiwizacja mostkuje na trasę zagnieżdżoną i potrzebuje `contractorId`
  // z załadowanego wiersza — ID spoza bieżącej strony nie miałoby skąd go wziąć (cichy
  // no-op raportowany jako porażka). Guard `===` długości chroni przed pętlą setState.
  useEffect(() => {
    setSelectedIds((prev) => {
      const present = prev.filter((id) => contacts.some((c) => c.id === id));
      return present.length === prev.length ? prev : present;
    });
  }, [contacts]);

  // Archiwizacja (miękkie usunięcie) kontaktów — kontakt zapisuje się WYŁĄCZNIE trasą
  // zagnieżdżoną `/contractors/:cId/contacts/:id` (płaska `/contractor-contacts/:id` to
  // tylko GET), więc generyczny fanout katalogu (`DELETE /:entity/:id`) tu nie trafia.
  // Rozgłaszamy DELETE po ID biorąc `contractorId` z załadowanego wiersza. Backend
  // liczy dostęp z rekordu (działa też w trybie „Wszystkie firmy" — bez X-Company-Id).
  const deleteContacts = async (ids: string[]) => {
    const rows = contacts.filter((c) => ids.includes(c.id));
    const results = await Promise.allSettled(
      rows.map((r) => api.delete(`/contractors/${r.contractorId}/contacts/${r.id}`)),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = ids.length - ok;
    queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] });
    queryClient.invalidateQueries({ queryKey: ['distinct-values', 'contractor_contacts'] });
    // Kontakt z zadaniem przycisku SQL w toku odrzuca usunięcie (409 `RECORD_LOCKED_BY_RUN`): zamiast samej
    // liczby nieudanych podajemy powód — jedno zadanie opisane (napis/godzina/osoba), kilka — ogólnie.
    const lockedTexts = results.flatMap((r) => {
      if (r.status !== 'rejected') return [];
      const text = recordLockReason(r.reason, t, i18n);
      return text === null ? [] : [text];
    });
    let lockedReason: string | undefined;
    if (lockedTexts.length === 1) {
      lockedReason = lockedTexts[0];
    } else if (lockedTexts.length > 1) {
      lockedReason = t('recordProcedureRuns.errors.lockedMany', { count: lockedTexts.length });
    }
    toast.show({
      variant: failed > 0 ? 'error' : 'success',
      title:
        failed > 0
          ? t('tenant.listActions.toast.partial', { ok, failed })
          : t('tenant.listActions.toast.done', { count: ok }),
      description: lockedReason,
    });
  };

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności + wykonawca + menu
  // kontekstowe + potwierdzenia. „Usuń" (miękkie, gcrecord) mostkuje `handlers.delete`
  // na trasę zagnieżdżoną (patrz wyżej), bo płaska trasa kontaktu jest GET-only.
  // Archiwum (archive/unarchive) NIE dotyczy kontaktów (tylko dokumenty). Eksport/import
  // → „wkrótce" (automatem hooka). Bez bramki „Wszystkie firmy": backend liczy dostęp z rekordu.
  const listActions = useListActions({
    kartotekaKey: 'contractor_contacts',
    entity: 'contractor-contacts',
    queryKey: [CONTACTS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: {
      delete: (ids) => deleteContacts(ids),
    },
    testIdPrefix: 'contractor-contacts',
  });

  return (
    <main className="min-h-full bg-bg-secondary p-6" data-testid="contractor-contacts-page">
      <div className="space-y-4">
        <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text-primary">
              {t('tenant.contractorContacts.title')}
            </h1>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('tenant.contractorContacts.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="contractor-contacts" />
            <ListActionsMenu {...listActions.menuProps} />
            <ListBuilderMenu slug="contractor_contacts" testIdPrefix="contractor-contacts" />
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(withMenuItem('/contractor-contacts/new'))}
              leftIcon={<Plus size={16} aria-hidden="true" />}
              data-testid="contractor-contacts-add-btn"
            >
              {t('tenant.contractorContacts.addBtn')}
            </Button>
          </div>
        </header>

        <ListTopWidgets tableId={tableId} categoryId={null} companyId={companyId} />

        <ConfigurableTable<ContactRow>
          tableId={tableId}
          testIdPrefix="contractor-contacts"
          showOrdinal
          {...viewModes.tableProps}
          /* Bez kalendarza (powód i deklaracja: `@/lib/list-view-support` — ta sama, z której
             korzysta Kreator Listy, więc zakładki „Kalendarz" tam też nie ma). Tabela zgłasza
             ten zbiór do nagłówka, więc pastylki kalendarza nie ma, a zapisany domyślny
             `calendar` jest klampowany do listy. */
          allowedViewModes={allowedViewModesForList('contractor_contacts')}
          /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): chipy grup po lewej,
             Akcje/Kreator/przełącznik widoku/odśwież po prawej; presety w osobnym rzędzie pod spodem. */
          listToolbar={
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {/* Chipy filtra = GRUPY ze słownika (wielokrotny wybór, OR) — zastąpiły chipy kategorii. */}
                <GroupChipBar
                  dictionaryCode="contractor_contact_groups"
                  selected={selectedGroups}
                  onChange={setSelectedGroups}
                  className="flex-1"
                  data-testid="contractor-contacts-group-chip-bar"
                />
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <ListFilterBar
                    tableId={tableId}
                    columns={columns}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder={t('tenant.contractorContacts.searchPlaceholder')}
                    showSearch={false}
                    headerFilters={headerFilters}
                    onHeaderFiltersChange={setHeaderFilters}
                    onRefresh={() => queryClient.invalidateQueries({ queryKey: [CONTACTS_KEY] })}
                    testIdPrefix="contractor-contacts"
                  />
                </div>
              </div>
              <QuickFilterBar
                quickFilters={quickFilters}
                activeId={activeQuickFilterId}
                onChange={setActiveQuickFilterId}
              />
            </div>
          }
          columns={columns}
          data={contacts}
          isLoading={listQ.isLoading}
          getRowId={(row) => row.id}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onRowContextMenu={listActions.onRowContextMenu}
          headerFilters={headerFilters}
          onHeaderFiltersChange={setHeaderFilters}
          filterable
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          onRowClick={(row) => navigate(withMenuItem(`/contractor-contacts/${row.id}`))}
          renderActions={(row) => (
            <div className="flex justify-end">
              <RowActionsMenu
                testId={`contractor-contacts-row-${row.id}-actions`}
                items={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    icon: Pencil,
                    onSelect: () => navigate(withMenuItem(`/contractor-contacts/${row.id}`)),
                    testId: `contractor-contacts-row-${row.id}-edit`,
                  },
                  // Akcje z katalogu list na TYM rekordzie — pod dividerem (PHASE 25):
                  // archiwizacja (miękkie usunięcie). Edytuj zostaje natywne u góry.
                  ...listActions.rowCatalogActionItems(row.id),
                ]}
              />
            </div>
          )}
          actionsColumnLabel={t('tenant.contractorContacts.col.actions')}
          emptyIcon={Users}
          emptyTitle={t('tenant.contractorContacts.empty')}
        />
      </div>

      {listActions.overlays}
    </main>
  );
}
